<template>
  <div>
    <ul v-if="metric.comparison && metric.value.period_label && metric.comparison.period_label" class="list-unstyled tw:flex tw:flex-wrap tw:gap-4 tw:mb-1">
      <li>
        <small class="text-muted">
          <i class="fa fa-circle" :style="{ color: COMPARISON_COLORS.current }"></i> {{ metric.value.period_label }}
        </small>
      </li>
      <li>
        <small class="text-muted">
          <i class="fa fa-circle" :style="{ color: COMPARISON_COLORS.compared }"></i> {{ metric.comparison.period_label }}
        </small>
      </li>
    </ul>

    <LineChart :datacollection="datacollection" :options="options" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LineChart from '@/components/charts/LineChart.vue';
import { COMPARISON_COLORS, type TrendMetricData } from './metrics';

const props = defineProps<{
  metric: TrendMetricData
}>();

/**
 * The compared series is laid over the current one bucket by bucket, so the
 * opening bucket of one window sits above the opening bucket of the other.
 * Nothing is filled under either line, so neither hides the other.
 */
const datacollection = computed(() => {
  const series = {
    fill: false,
    borderWidth: 2,
    pointHitRadius: 4,
  };

  return {
    labels: props.metric.value.points.map((point) => point.starts_at),
    datasets: [
      {
        ...series,
        label: props.metric.value.period_label,
        borderColor: COMPARISON_COLORS.current,
        data: props.metric.value.points.map((point) => point.value),
      },
      ...(props.metric.comparison ? [{
        ...series,
        label: props.metric.comparison.period_label,
        borderColor: COMPARISON_COLORS.compared,
        data: props.metric.value.points.map((_, index) => props.metric.comparison!.points[index]?.value ?? null),
      }] : []),
    ],
  };
});

const options = computed(() => ({
  responsive: true,
  scales: {
    x: {
      type: 'time',
      time: { unit: props.metric.value.interval === 'half_hour' ? 'hour' : props.metric.value.interval },
      grid: { display: false },
    },
    y: {
      display: true,
      beginAtZero: true,
      ticks: { stepSize: 1 },
      grid: { display: false },
    },
  },
  elements: {
    point: { radius: 1, hitRadius: 4, pointStyle: false },
  },
  interaction: { intersect: false, axis: 'x' },
  plugins: {
    legend: { display: false },
  },
}));
</script>
